import React from "react";

import type { LaneRendererProps } from "../lane.types";
import { itemVisualDensity, productTone, readNumberMeta, readStringMeta, visibleLaneItems } from "../product/rendering";

function stateChip(label: string, tone: "sky" | "amber" | "rose" | "slate") {
  const className =
    tone === "sky"
      ? "border-sky-700/16 bg-sky-50/92 text-sky-950"
      : tone === "amber"
        ? "border-amber-700/16 bg-amber-50/92 text-amber-900"
        : tone === "rose"
          ? "border-rose-700/16 bg-rose-50/92 text-rose-900"
          : "border-slate-700/16 bg-slate-50/92 text-slate-900";

  return <span className={["rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]", className].join(" ")}>{label}</span>;
}

export default function SegmentLaneRenderer({ lane, items, viewport }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);

  return (
    <div className="relative h-full min-h-[48px]" data-lane-class="segment" data-lane-id={lane.id} data-lane-items={items.length}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-slate-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const density = itemVisualDensity(widthPx);
          const label = item.itemType === "segment" ? item.label : item.id;
          const proxyStatus = readStringMeta(item, "proxyStatus");
          const offlineState = readStringMeta(item, "offlineState");
          const analysisState = readStringMeta(item, "analysisState") ?? readStringMeta(item, "status");
          const confidence = readNumberMeta(item, "confidence") ?? readNumberMeta(item, "metadataRichnessScore");
          const segmentCount = Math.max(3, Math.min(14, Math.round(widthPx / 20)));

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 h-10 -translate-y-1/2 overflow-hidden rounded-xl border border-slate-300/24 bg-[linear-gradient(135deg,rgba(148,163,184,0.18),rgba(51,65,85,0.20),rgba(2,6,23,0.22))] px-3 shadow-[0_8px_20px_rgba(15,23,42,0.14)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              <div className="absolute inset-x-2 bottom-[6px] flex h-5 items-end gap-[2px] opacity-80">
                {Array.from({ length: segmentCount }, (_, index) => (
                  <div
                    key={index}
                    className="min-w-0 flex-1 rounded-full bg-slate-100/24"
                    style={{ height: `${Math.max(22, 26 + ((index % 5) * 12))}%` }}
                  />
                ))}
              </div>
              <div className="relative flex h-full items-center justify-between gap-2">
                <div className="min-w-0">
                  {density !== "micro" ? (
                    <>
                      <div className="truncate text-[11px] font-semibold text-slate-50">{label}</div>
                      <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                        {proxyStatus ? stateChip(proxyStatus.replace(/_/g, " "), "sky") : null}
                        {offlineState && offlineState !== "online" ? stateChip(offlineState, "rose") : null}
                        {analysisState ? stateChip(analysisState.replace(/_/g, " "), analysisState === "failed" ? "rose" : "amber") : null}
                        {typeof confidence === "number" ? stateChip(`${Math.round(confidence * 100)}%`, "slate") : null}
                      </div>
                    </>
                  ) : (
                    <div className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-50/88">Seg</div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="grid h-full place-items-center px-3">
          <div className={["rounded-full border px-3 py-1 text-[10px] font-semibold", tone.emptyClassName].join(" ")}>No segments in view</div>
        </div>
      )}
    </div>
  );
}